import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Trophy, Clock, Target, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function TestResults() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = location.state?.returnPath || '/dashboard';

  useEffect(() => {
    document.title = 'Test Results | Practice4SAT';
  }, []);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExitDialog, setShowExitDialog] = useState(false);

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      // Check if we have cached scores from completion
      const cachedScore = sessionStorage.getItem(`test_score_${sessionId}`);
      const cachedRwScore = sessionStorage.getItem(`test_rw_score_${sessionId}`);
      const cachedMathScore = sessionStorage.getItem(`test_math_score_${sessionId}`);
      
      // Try to get diagnostic session first
      try {
        const diagnosticResponse = await axios.get(`${API}/diagnostic/${sessionId}/review`);
        if (diagnosticResponse.data) {
          // This is a diagnostic test - format data to match regular test structure
          const data = diagnosticResponse.data;
          
          // Get cached diagnostic scores
          const diagFinalScore = localStorage.getItem('diagnostic_final_score');
          const diagRwScore = localStorage.getItem('diagnostic_rw_score');
          const diagMathScore = localStorage.getItem('diagnostic_math_score');
          
          setResults({
            session: {
              id: sessionId,
              test_id: 'diagnostic',
              final_score: data.final_score || (diagFinalScore ? parseInt(diagFinalScore) : null),
              rw_score: data.rw_score || (diagRwScore ? parseInt(diagRwScore) : null),
              math_score: data.math_score || (diagMathScore ? parseInt(diagMathScore) : null),
              completed_at: data.completed_at
            },
            test_title: data.test_title || 'Diagnostic Test',
            rw_score: data.rw_score || (diagRwScore ? parseInt(diagRwScore) : null),
            math_score: data.math_score || (diagMathScore ? parseInt(diagMathScore) : null),
            questions: data.questions,
            analytics: {
              by_difficulty: calculateDifficulty(data.questions),
              total_time: data.questions.reduce((sum, q) => sum + (q.time_spent || 0), 0),
              average_time_per_question: data.questions.length > 0 
                ? data.questions.reduce((sum, q) => sum + (q.time_spent || 0), 0) / data.questions.length 
                : 0
            }
          });
          return;
        }
      } catch (error) {
        console.debug('Not a diagnostic session, loading regular test');
      }
      
      // Regular test session
      const sessions = await axios.get(`${API}/test-sessions/${sessionId}`);
      const testId = sessions.data.test_id;
      
      const sessionResponse = await axios.get(`${API}/tests/${testId}/results/${sessionId}`);
      
      // If we have cached scores and the API doesn't return them, use cached
      if (cachedScore && !sessionResponse.data?.session?.final_score) {
        sessionResponse.data.session.final_score = parseInt(cachedScore);
      }
      if (cachedRwScore && !sessionResponse.data?.session?.rw_score) {
        sessionResponse.data.session.rw_score = parseInt(cachedRwScore);
        sessionResponse.data.rw_score = parseInt(cachedRwScore);
      }
      if (cachedMathScore && !sessionResponse.data?.session?.math_score) {
        sessionResponse.data.session.math_score = parseInt(cachedMathScore);
        sessionResponse.data.math_score = parseInt(cachedMathScore);
      }
      
      setResults(sessionResponse.data);
      
      // Clear cached scores after using them
      if (cachedScore) {
        sessionStorage.removeItem(`test_score_${sessionId}`);
        sessionStorage.removeItem(`test_rw_score_${sessionId}`);
        sessionStorage.removeItem(`test_math_score_${sessionId}`);
      }
    } catch (error) {
      toast.error('Failed to load results');
      navigate(backPath);
    } finally {
      setLoading(false);
    }
  };

  const calculateDifficulty = (questions) => {
    const byDifficulty = {
      easy: { correct: 0, total: 0 },
      medium: { correct: 0, total: 0 },
      hard: { correct: 0, total: 0 }
    };
    
    questions.forEach(q => {
      const diff = q.difficulty || 'medium';
      byDifficulty[diff].total += 1;
      if (q.is_correct) {
        byDifficulty[diff].correct += 1;
      }
    });
    
    return byDifficulty;
  };
  
  const retakeTest = () => {
    if (results?.session?.test_id === 'diagnostic') {
      // Can't retake diagnostic test - navigate to dashboard
      toast.error('Diagnostic test can only be taken once');
      navigate(backPath);
    } else if (results?.session?.test_id) {
      navigate(`/test/${results.session.test_id}`, { state: { returnPath: backPath } });
    }
  };

  const handleExit = () => {
    navigate(backPath);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading results...</div>
      </div>
    );
  }

  if (!results || !results.session) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>No results found</div>
      </div>
    );
  }

  const { session } = results;
  const correctCount = results.questions?.filter(q => q.is_correct).length || 0;
  const totalQuestions = results.questions?.length || 0;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header variant="minimal" logoClickPath="/dashboard" />

      {/* Main Content */}
      <div style={{ flex: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ 
          background: 'white', 
          borderRadius: '16px', 
          padding: '3rem', 
          maxWidth: '600px', 
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          textAlign: 'center'
        }}>
          {/* Trophy Icon */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginBottom: '1.5rem' 
          }}>
            <div style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '50%',
              padding: '1.5rem',
              display: 'inline-flex'
            }}>
              <Trophy size={48} color="white" />
            </div>
          </div>

          {/* Title */}
          <h1 style={{ 
            fontSize: '2rem', 
            fontWeight: '700', 
            marginBottom: '0.5rem',
            color: '#1a202c'
          }}>
            Test Complete!
          </h1>

          <p style={{ 
            fontSize: '1rem', 
            color: '#666', 
            marginBottom: '2rem' 
          }}>
            Congratulations on completing the test
          </p>

          {/* Score Display */}
          {/* Check if this is a full SAT test with both sections */}
          {(session?.rw_score || results?.rw_score) ? (
            <>
              {/* Full SAT Score Display */}
              <div style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '12px',
                padding: '2rem',
                marginBottom: '1rem'
              }}>
                <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                  Your Total SAT Score
                </div>
                <div style={{ 
                  fontSize: '4rem', 
                  fontWeight: '700', 
                  color: 'white',
                  lineHeight: 1
                }}>
                  {session?.final_score || 'N/A'}
                </div>
                <div style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.9)', marginTop: '0.5rem' }}>
                  out of 1600
                </div>
              </div>

              {/* Section Scores */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                {/* R&W Score */}
                <div style={{ 
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.9)', marginBottom: '0.25rem' }}>
                    Reading & Writing
                  </div>
                  <div style={{ 
                    fontSize: '2.5rem', 
                    fontWeight: '700', 
                    color: 'white',
                    lineHeight: 1
                  }}>
                    {session?.rw_score || results?.rw_score || 'N/A'}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)', marginTop: '0.25rem' }}>
                    out of 800
                  </div>
                </div>

                {/* Math Score */}
                <div style={{ 
                  background: 'linear-gradient(135deg, #0030b3 0%, #3b82f6 100%)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.9)', marginBottom: '0.25rem' }}>
                    Math
                  </div>
                  <div style={{ 
                    fontSize: '2.5rem', 
                    fontWeight: '700', 
                    color: 'white',
                    lineHeight: 1
                  }}>
                    {session?.math_score || results?.math_score || 'N/A'}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)', marginTop: '0.25rem' }}>
                    out of 800
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Legacy Math-only Score Display */
            <div style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px',
              padding: '2rem',
              marginBottom: '1rem'
            }}>
              <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                Your SAT Score
              </div>
              <div style={{ 
                fontSize: '4rem', 
                fontWeight: '700', 
                color: 'white',
                lineHeight: 1
              }}>
                {session?.final_score || 'N/A'}
              </div>
              <div style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.9)', marginTop: '0.5rem' }}>
                out of 800
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <Button 
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-btn"
              style={{ 
                flex: 1,
                padding: '1rem',
                fontSize: '0.95rem',
                fontWeight: '400'
              }}
            >
              Back to Dashboard
            </Button>
            <Button 
              onClick={() => navigate('/tests')}
              variant="outline"
              data-testid="back-to-tests-btn"
              style={{ 
                flex: 1,
                padding: '1rem',
                fontSize: '0.95rem',
                fontWeight: '400'
              }}
            >
              Back to Full-Length Tests
            </Button>
          </div>

          <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '1rem' }}>
            View detailed analytics and review questions from your dashboard
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
